import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

const projectId = import.meta.env.VITE_SANITY_PROJECT_ID;
const dataset = import.meta.env.VITE_SANITY_DATASET;
const apiVersion = import.meta.env.VITE_SANITY_API_VERSION || "2024-06-01";
const useCdn = import.meta.env.VITE_SANITY_USE_CDN !== "false";

export const isSanityConfigured = Boolean(projectId && dataset);

export const sanityClient = createClient({
  projectId: projectId || "missing-project-id",
  dataset: dataset || "missing-dataset",
  apiVersion,
  useCdn,
});

export const sanityLiveClient = sanityClient.withConfig({ useCdn: false });

const builder = imageUrlBuilder(sanityClient);
export const urlFor = (source: unknown) => builder.image(source);

export async function fetchSanity<T>(query: string, params?: Record<string, unknown>): Promise<T> {
  if (!isSanityConfigured) {
    throw new Error("Sanity is not configured. Set VITE_SANITY_PROJECT_ID and VITE_SANITY_DATASET.");
  }
  return sanityClient.fetch<T>(query, params);
}

export const siteSettingsQuery = `
  *[_type == "siteSettings"][0]{
    firstName,
    lastName,
    title,
    description,
    location,
    email,
    githubUrl,
    linkedinUrl,
    instagramUrl,
    twitterUrl,
    heroPrimaryCta,
    heroSecondaryCta,
    navigationBrand,
    navigationItems[]{
      label,
      sectionId,
      isEnabled,
      sortOrder
    },
    aboutHeading,
    aboutFocusHeading,
    aboutInterestsHeading,
    aboutIntro,
    professionalFocus,
    personalInterests[]{icon, label},
    projectsHeading,
    projectsIntro,
    skillsHeading,
    competenciesHeading,
    skillsIntro,
    contactHeading,
    contactFormHeading,
    contactIntro,
    blogHeading,
    blogIntro,
    blogCtaLabel,
    blogCtaUrl,
    blogSectionEnabled,
    footerTagline,
    footerStatus,
    footerUptime,
    "profileImageUrl": coalesce(profileImage.asset->url, profileImageUrl)
  }
`;

export const projectsQuery = `
  *[_type == "project"] | order(sortOrder asc, _createdAt desc) {
    "id": _id,
    title,
    description,
    technologies,
    repositoryUrl,
    liveUrl,
    stars,
    forks,
    "imageUrl": coalesce(image.asset->url, imageUrl)
  }
`;

export const skillsQuery = `
  *[_type == "skill"] | order(sortOrder asc, _createdAt desc) {
    "id": _id,
    name,
    description,
    category,
    level,
    color,
    "iconUrl": coalesce(icon.asset->url, iconUrl)
  }
`;

export const competenciesQuery = `
  *[_type == "competency"] | order(sortOrder asc, _createdAt desc) {
    "id": _id,
    title,
    description,
    icon
  }
`;

export const blogPostsQuery = `
  *[_type == "blogPost" && published == true] | order(publishedAt desc, _createdAt desc) {
    "id": _id,
    title,
    excerpt,
    "slug": slug.current,
    publishedAt,
    tags,
    "imageUrl": coalesce(image.asset->url, imageUrl)
  }
`;
